import React, { Component, createRef } from 'react';
import { connect } from 'react-redux';
import { ReactSVG } from 'react-svg';
import classNames from 'classnames';
import _throttle from 'lodash/throttle';
import ProptTypes from 'prop-types';

import { getStateName,ismobileDevice, openInNewTab } from '../../../utils';
import { logEventGTM } from '../../../utils/analyticsGTM';

import classes from './index.module.css';

const COUNTRY_MAP_SVG_CLASS = classes['county-map-svg'] || 'county-map-svg';

const loadingMapClassList = {
  background: classes['loading-county-map-background'],
  animation: classes['loading-county-map-animation'],
};

const mapToolTipPropType = ProptTypes.shape({
  title: ProptTypes.string,
  valueFormatFunc: ProptTypes.func,
});

const propTypes = {
  onMapLoadError: ProptTypes.func,
  onMapLoadSuccess: ProptTypes.func,
  targetPageUrl: ProptTypes.string,
  svgFileUrl: ProptTypes.string,
  originalUrl: ProptTypes.string,
  gtmEventName: ProptTypes.objectOf(ProptTypes.any),
  mapTooltip: ProptTypes.objectOf(ProptTypes.any),
  mapTooltipLine2: mapToolTipPropType,
  isEmbedWidgetOpen: ProptTypes.bool.isRequired,
};

const defaultProps = {
  onMapLoadError: () => {},
  onMapLoadSuccess: () => {},
  targetPageUrl: '',
  svgFileUrl: '',
  originalUrl: '',
  gtmEventNames: {
    open: '',
  },
  mapTooltip: {
    title: '',
    valueFormatFunc: () => {},
  },
};

const HOVER_BLOCK_DELAY = {
  MOUSE_MOVE_BEGIN: 20,
  MOUSE_MOVE_END: 20,
  CONTENT_UPDATE_FREQUENCY: 200,
};
const HOVER_BLOCK_GAP = 36;

class TitleSalaryOnlyStateMapTemplate extends Component {
  constructor(props) {
    super(props);

    this.mapInteractedFlag = false;

    this.mapImgTagRef = createRef();
    this.mapOuterContainerRef = createRef();
    this.hoverBlockRef = createRef();
    this.hoverBlockOverlayRef = createRef();
    this.blockLocationRef = createRef();
    this.blockValueRef = createRef();
    this.blockValueRef2 = createRef();
  }

  componentDidMount() {
    const { checkImgTagLoad } = this;
    // check if image already cached
    checkImgTagLoad();
  }

  checkImgTagLoad = () => {
    const { mapImgTagRef, onImgTagLoad } = this;
    const mapImgTag = mapImgTagRef && mapImgTagRef.current;

    if (mapImgTag && mapImgTag.complete) {
      onImgTagLoad();
    }
  };

  onImgTagLoad = () => {
    const { mapOuterContainerRef = {} } = this;

    const mapOuterContainer = mapOuterContainerRef && mapOuterContainerRef.current;
    if (mapOuterContainer) {
      mapOuterContainer.classList.remove(loadingMapClassList.background);
    }
  };

  onSvgInjected = (svg) => {
    const { mapOuterContainerRef = {}, prepareMap } = this;
    const { onMapLoadSuccess } = this.props;

    prepareMap(svg);
    onMapLoadSuccess(svg);

    const mapOuterContainer = mapOuterContainerRef && mapOuterContainerRef.current;
    if (mapOuterContainer) {
      mapOuterContainer.classList.remove(loadingMapClassList.animation);
      mapOuterContainer.classList.remove(loadingMapClassList.background);
    }
  };

  shouldComponentUpdate(nextProps) {
    const { isEmbedWidgetOpen } = this.props;
    return ismobileDevice() && nextProps.isEmbedWidgetOpen !== isEmbedWidgetOpen;
  }

  onMapInteraction = () => {
    const { gtmEventNames } = this.props;
    if (!this.mapInteractedFlag) {
      if (gtmEventNames.open) {
        logEventGTM({
          event: gtmEventNames.open,
        });
      }
      this.mapInteractedFlag = true;
    }
  };

  prepareMap = (svg) => {
    const {
      setHoverBlockVisibility,
      updateHoverBlockContent,
      handleCountyClick,
      onMapInteraction,
      hoverBlockRef = {},
    } = this;

    if (svg) {
      const svgStyles = svg.getElementById('style_css_sheet');
      if (svgStyles) svgStyles.innerHTML = `.${COUNTRY_MAP_SVG_CLASS} ${svgStyles.innerHTML}`;
    }

    const hoverBlock = hoverBlockRef && hoverBlockRef.current;

    let prevTarget = null;

    const handleHoverBlockPosition = (e) => {
      const { _isEventFromCroll = false, target: _target, originalTarget } = e;
      const target =
        (originalTarget?.id && originalTarget) ||
        (_target &&
          (_target.id
            ? _target
            : [...(_target?.parentElement?.classList || [])].find((x) => x.includes('sta')) &&
              _target.parentElement));
      const { id } = target || {};
      if (
        id &&
        target?.dataset?.value !== undefined &&
        !['style_css_sheet', 'divider', 'divider_1_'].includes(id)
      ) {
        setHoverBlockVisibility(true);

        const svgRect = svg.getBoundingClientRect();
        const targetRect = target.getBoundingClientRect();
        const hoverBlockRect = hoverBlock.getBoundingClientRect();

        hoverBlock.className = classes['hover-block'];
        hoverBlock.style.position = 'fixed';
        hoverBlock.style.right = 'auto';

        const hoverBlockClassNameOverflownX = classes['hover-block-overflown-x'];
        const hoverBlockClassNameOverflownY = classes['hover-block-overflown-y'];

        const GAP = ismobileDevice() ? 0 : 5;
        const clientY = _isEventFromCroll ? targetRect.top + 20 : e.clientY;

        if (targetRect.top < svgRect.height / 2) {
          hoverBlock.style.top = clientY + HOVER_BLOCK_GAP + hoverBlockRect.height + 'px';
          hoverBlock.className += ` ${hoverBlockClassNameOverflownY}`;
        } else hoverBlock.style.top = clientY - GAP + 'px';

        if (targetRect.left + targetRect.height / 2 > svgRect.width / 2) {
          hoverBlock.className += ` ${hoverBlockClassNameOverflownX}`;
          hoverBlock.style.left = e.clientX - hoverBlockRect.width + 'px';
        } else hoverBlock.style.left = e.clientX + GAP + 'px';

        target.style.strokeWidth = '4';
        target.onmouseleave = () => {
          target.style.strokeWidth = '1.5';
          target.onmouseleave = null;
        };
        prevTarget = target;
        onMapInteraction();
      } else {
        setHoverBlockVisibility(false);
      }
    };

    const checkScrollPosition = () => {
      setHoverBlockVisibility(false);
    };

    const positionUpdateDelay = ismobileDevice() ? 0 : HOVER_BLOCK_DELAY.MOUSE_MOVE_BEGIN;
    const contentUpdateDelay = ismobileDevice() ? 0 : HOVER_BLOCK_DELAY.CONTENT_UPDATE_FREQUENCY;

    const handleMouseMoveThrottle = _throttle(handleHoverBlockPosition, positionUpdateDelay);
    const updateHoverBlockContentThrottle = _throttle(updateHoverBlockContent, contentUpdateDelay);
    const checkScrollPositionThrottle = _throttle(checkScrollPosition, 300);

    if (ismobileDevice()) {
      svg &&
        svg.addEventListener('click', (e) => {
          handleMouseMoveThrottle(e);
          updateHoverBlockContentThrottle(e);
        });
      window.document.addEventListener(
        'scroll',
        (e) =>
          prevTarget &&
          handleMouseMoveThrottle({ ...e, _isEventFromCroll: true, originalTarget: prevTarget })
      );
    } else {
      svg &&
        svg.addEventListener('mousemove', (e) => {
          handleMouseMoveThrottle(e);
          updateHoverBlockContentThrottle(e);
        });
      window.document.addEventListener('scroll', checkScrollPositionThrottle);
    }

    if (hoverBlock) {
      svg.onmouseleave = () => {
        setHoverBlockVisibility(false);
      };
    }

    svg.onclick = (e) => {
      handleCountyClick(e);
    };
  };

  handleCountyClick = () => {
    const { targetPageUrl } = this.props;
    if (targetPageUrl) {
      openInNewTab(targetPageUrl);
    }
  };

  updateHoverBlockContent = (e) => {
    const { mapTooltip, mapTooltipLine2 } = this.props;
    const {
      blockLocationRef = {},
      blockValueRef = {},
      hoverBlockRef = {},
      blockValueRef2 = {},
      formatCountyName,
    } = this;

    const { valueFormatFunc = () => {}, subTextFormatFunc = () => '' } = mapTooltip || {};
    const valueFormatFunc2 = mapTooltipLine2?.valueFormatFunc || (() => {/* do nothing */});

    const { target: _target, originalTarget } = e;
    const target =
      (originalTarget?.id && originalTarget) ||
      (_target &&
        (_target.id
          ? _target
          : [...(_target?.parentElement?.classList || [])].find((x) => x.includes('sta')) &&
            _target.parentElement));
    if (!target?.id) return;
    const stateLocation = blockLocationRef.current;
    const stateValue = blockValueRef.current;
    if (stateLocation) {
      stateLocation.dataset.currentid = target.id;
      stateLocation.innerHTML = formatCountyName(target.id.slice(0, 2));
    }
    if (stateValue) {
      stateValue.innerHTML = valueFormatFunc(target.dataset.value);
    }
    const subTextElm = hoverBlockRef?.current?.querySelector('.hover-sub-text');
    if (subTextElm) {
      subTextElm.innerHTML = subTextFormatFunc(target.id, parseInt(target?.dataset?.value ?? 0));
    }
    const countyValue2 = blockValueRef2?.current;
    if (countyValue2) {
      const stateCode = target.getAttribute('id').split(/[-_]/)[0]?.toUpperCase();
      countyValue2.innerHTML = valueFormatFunc2(stateCode);
    }
  };

  setHoverBlockVisibility = (isVisible) => {
    const { hoverBlockRef = {}, hoverBlockOverlayRef = {} } = this;
    const hoverBlock = hoverBlockRef.current || {};
    const hoverBlockOverlay = hoverBlockOverlayRef.current || {};
    if (hoverBlock.style) {
      hoverBlock.style.visibility = isVisible ? 'visible' : 'hidden';
    }
    if (hoverBlockOverlay.style) {
      hoverBlockOverlay.style.visibility = isVisible ? 'visible' : 'hidden';
    }
  };

  formatCountyName = (countyName) => getStateName(countyName);

  getHoverBlockView = () => {
    const { mapTooltip, mapTooltipLine2 } = this.props;
    const {
      setHoverBlockVisibility,
      hoverBlockRef,
      hoverBlockOverlayRef,
      blockLocationRef,
      blockValueRef,
      blockValueRef2,
    } = this;
    const { title = '' } = mapTooltip || {};
    const title2 = mapTooltipLine2?.title || '';
    return (
      <>
        <div
          ref={hoverBlockOverlayRef}
          className={classes['hover-block-mobile-overlay']}
          onClick={() => setHoverBlockVisibility(false)}
        />
        <div
          ref={hoverBlockRef}
          className={classes['hover-block']}
          onClick={() => setHoverBlockVisibility(false)}
        >
          <div className={classes['hover-block-location-row']}>
            <i className="icon-map" />
            <span className={classes['hover-block-location-name']} ref={blockLocationRef} />
          </div>
          <div className={classNames(classes['hover-block-col'], 'pl-4')}>
            {title && (
                <span className={classes['hover-block-title']}>
                  <span className={classes['title-block']}>{title + ': '}</span>{' '}
                  <span
                    className={classNames(classes['hover-block-value'], 'text-white')}
                    ref={blockValueRef}
                  />
                </span>
            )}
            {mapTooltipLine2 && (
              <span className={classes['hover-block-title']}>
                <span className={classes['title-block']}>{title2}</span>
                <span className={classes['hover-block-value']} ref={blockValueRef2} />
              </span>
            )}
            <div className={classes['hover-sub-text']} />
          </div>
        </div>
      </>
    );
  };

  getMapView = () => {
    const { onSvgInjected, mapOuterContainerRef, mapImgTagRef, onImgTagLoad } = this;
    const { onMapLoadError, svgFileUrl, originalUrl } = this.props;

    return (
      <div
        className={`${classes['county-map-outer']} ${loadingMapClassList.background} ${loadingMapClassList.animation}`}
        ref={mapOuterContainerRef}
      >
        <img
          ref={mapImgTagRef}
          className={classes['county-map-img']}
          onLoad={onImgTagLoad}
          src={originalUrl}
        />
        <ReactSVG
          src={svgFileUrl}
          className={COUNTRY_MAP_SVG_CLASS}
          afterInjection={(error, svg) => {
            if (error) {
              onMapLoadError(error);
              return;
            }
            onSvgInjected(svg);
          }}
        />
      </div>
    );
  };

  render() {
    const { getHoverBlockView, getMapView } = this;

    const hoverBlockView = getHoverBlockView();
    const mapView = getMapView();

    return (
      <>
        {hoverBlockView}
        {mapView}
      </>
    );
  }
}

const mapStateToProps = ({ modals: { embedWidget } }) => {
  const { open } = embedWidget;
  return {
    isEmbedWidgetOpen: open,
  };
};

TitleSalaryOnlyStateMapTemplate.propTypes = propTypes;
TitleSalaryOnlyStateMapTemplate.defaultProps = defaultProps;

export default connect(mapStateToProps)(TitleSalaryOnlyStateMapTemplate);
